/**
 * Class Schedules API - Direct Supabase fetch functions
 * Replaces period-templates-direct.ts after schema redesign.
 * Requirements: 23.1, 29.1, 29.2, 19.3
 */

/// <reference lib="dom" />

import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token';
import type {
  ClassScheduleWithPeriods,
  CreateClassScheduleRequest,
  UpdateClassScheduleRequest,
  AssignScheduleToClassesRequest,
} from '@timetable/types';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

function authHeaders() {
  const token = getAccessToken();
  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function restGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${SUPABASE_URL}/rest/v1/${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `GET ${path} failed`);
  }
  return res.json();
}

async function restPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Prefer': 'return=representation' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `POST ${path} failed`);
  }
  return res.json();
}

async function restPatch(path: string, body: unknown): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Prefer': 'return=minimal' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `PATCH ${path} failed`);
  }
}

async function restDelete(path: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `DELETE ${path} failed`);
  }
}

/** Fetch all class schedules for a school */
export async function getClassSchedulesDirect(schoolId: string): Promise<ClassScheduleWithPeriods[]> {
  return handleApiCall(async () => {
    const schedules = await restGet<any[]>('class_schedules', {
      select: '*,schedule_periods(*)',
      school_id: `eq.${schoolId}`,
      order: 'name.asc',
    });
    return schedules as ClassScheduleWithPeriods[];
  });
}

/** Fetch a single class schedule by ID */
export async function getClassScheduleDirect(id: string): Promise<ClassScheduleWithPeriods> {
  return handleApiCall(async () => {
    const rows = await restGet<any[]>('class_schedules', {
      select: '*,schedule_periods(*)',
      id: `eq.${id}`,
    });
    if (!rows || rows.length === 0) throw new Error('Class schedule not found');
    return rows[0] as ClassScheduleWithPeriods;
  });
}

/** Create a new class schedule with its schedule periods */
export async function createClassScheduleDirect(
  request: CreateClassScheduleRequest
): Promise<ClassScheduleWithPeriods> {
  return handleApiCall(async () => {
    const { schedule_periods, ...scheduleData } = request;

    const created = await restPost<any[]>('class_schedules', scheduleData);
    const schedule = created[0];

    if (schedule_periods && schedule_periods.length > 0) {
      const periodsToInsert = schedule_periods.map(p => ({ ...p, class_schedule_id: schedule.id }));
      await restPost('schedule_periods', periodsToInsert);
    }

    return getClassScheduleDirect(schedule.id);
  });
}

/** Update an existing class schedule (replaces all schedule_periods if provided) */
export async function updateClassScheduleDirect(
  id: string,
  request: UpdateClassScheduleRequest
): Promise<ClassScheduleWithPeriods> {
  return handleApiCall(async () => {
    const { schedule_periods, ...scheduleData } = request;

    if (Object.keys(scheduleData).length > 0) {
      await restPatch(`class_schedules?id=eq.${id}`, scheduleData);
    }

    if (schedule_periods !== undefined) {
      await restDelete(`schedule_periods?class_schedule_id=eq.${id}`);
      if (schedule_periods.length > 0) {
        const periodsToInsert = schedule_periods.map(p => ({ ...p, class_schedule_id: id }));
        await restPost('schedule_periods', periodsToInsert);
      }
    }

    return getClassScheduleDirect(id);
  });
}

/** Delete a class schedule — blocked if any class is using it */
export async function deleteClassScheduleDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const classes = await restGet<any[]>('classes', {
      select: 'id,class_name',
      class_schedule_id: `eq.${id}`,
    });

    if (classes && classes.length > 0) {
      const names = classes.map((c: any) => c.class_name).join(', ');
      throw new Error(`Cannot delete schedule: it is used by ${classes.length} class(es): ${names}`);
    }

    await restDelete(`class_schedules?id=eq.${id}`);
  });
}

/** Bulk assign a schedule to multiple classes */
export async function assignScheduleToClassesDirect(
  request: AssignScheduleToClassesRequest
): Promise<{ updated: number }> {
  return handleApiCall(async () => {
    const updateData: Record<string, any> = { class_schedule_id: request.schedule_id };

    const ids = request.class_ids.map(id => `"${id}"`).join(',');
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/classes?id=in.(${ids})`,
      {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Prefer': 'return=representation' },
        body: JSON.stringify(updateData),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to assign schedule to classes');
    }
    const updated = await res.json();
    return { updated: updated?.length ?? 0 };
  });
}

/** Get classes that use a specific schedule */
export async function getClassesUsingSchedule(scheduleId: string): Promise<{ id: string; class_name: string }[]> {
  return handleApiCall(async () => {
    return restGet<{ id: string; class_name: string }[]>('classes', {
      select: 'id,class_name',
      class_schedule_id: `eq.${scheduleId}`,
    });
  });
}

/** Fetch schedule_periods for a given class_schedule_id — used for time display in the grid */
export async function getSchedulePeriodsByScheduleId(scheduleId: string): Promise<import('@timetable/types').SchedulePeriod[]> {
  return handleApiCall(async () => {
    return restGet<import('@timetable/types').SchedulePeriod[]>('schedule_periods', {
      select: '*',
      class_schedule_id: `eq.${scheduleId}`,
      order: 'period_number.asc',
    });
  });
}

/**
 * Get the number of timetable records referencing a specific schedule_period.
 * Used to enforce immutability before delete/update of timing fields (Req 19.3).
 */
export async function getSchedulePeriodUsageCount(schedulePeriodId: string): Promise<number> {
  return handleApiCall(async () => {
    const token = getAccessToken();
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?period_id=eq.${schedulePeriodId}&select=id`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Prefer': 'count=exact',
          'Range-Unit': 'items',
          'Range': '0-0',
        },
      }
    );
    if (!res.ok) return 0;
    const contentRange = res.headers.get('Content-Range');
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)$/);
      if (match) return parseInt(match[1], 10);
    }
    const data: any[] = await res.json();
    return data.length;
  });
}

/** Create a single schedule_period for a given class_schedule_id */
export async function createSchedulePeriodDirect(
  classScheduleId: string,
  period: Omit<import('@timetable/types').SchedulePeriod, 'id' | 'class_schedule_id' | 'created_at' | 'updated_at'>
): Promise<import('@timetable/types').SchedulePeriod> {
  return handleApiCall(async () => {
    const rows = await restPost<import('@timetable/types').SchedulePeriod[]>('schedule_periods', {
      ...period,
      class_schedule_id: classScheduleId,
    });
    return (rows as any[])[0];
  });
}
